/* Falcon theme by Saar LLC (https://saar-llc.com). Copyright (c) 2026 Saar LLC. All rights reserved. */
/* Announcement bar (original code): rotating messages with pause on hover/focus, editor block select. */
(() => {
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.setupClose();
      this.setupSticky();
      if (this.dataset.mode === 'marquee') return;
      this.slides = [...this.querySelectorAll('.abar__slide')];
      if (this.slides.length < 2) return;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.delay = (Number(this.dataset.interval) || 5) * 1000;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.querySelector('[data-abar-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
      this.querySelector('[data-abar-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stop()));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.restart()));
      this.section = this.closest('.shopify-section');
      this.onBlock = (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target || s.dataset.blockId === e.detail?.blockId || s.matches(`[data-shopify-editor-block*="${e.detail?.blockId}"]`));
        if (i > -1) { this.go(i); this.stop(); }
      };
      this.onDeselect = () => this.restart();
      this.section?.addEventListener('shopify:block:select', this.onBlock);
      this.section?.addEventListener('shopify:block:deselect', this.onDeselect);
      // Skip slides whose countdown expired and hid itself.
      this.addEventListener('countdown:expired', () => { if (this.current()?.querySelector('[hidden].abar__item, .abar__item[hidden]')) this.go(this.index + 1); });
      this.restart();
    }
    setupClose() {
      const btn = this.querySelector('[data-abar-close]');
      const key = this.dataset.closeKey;
      if (!btn || !key) return;
      btn.addEventListener('click', () => {
        try { (this.dataset.closeMemory === 'always' ? localStorage : sessionStorage).setItem(key, '1'); } catch (e) { /* storage blocked */ }
        this.hidden = true;
        this.syncSticky();
      });
    }
    setupSticky() {
      if (!this.classList.contains('abar--sticky')) return;
      this.syncSticky = this.syncSticky.bind(this);
      this.ro = new ResizeObserver(this.syncSticky);
      this.ro.observe(this);
      this.syncSticky();
    }
    syncSticky() {
      if (!this.classList.contains('abar--sticky')) return;
      const h = this.hidden || !this.offsetParent ? 0 : this.offsetHeight;
      if (h === this.lastH) return;
      this.lastH = h;
      document.documentElement.style.setProperty('--abar-sticky-h', `${h}px`);
      window.dispatchEvent(new Event('resize'));
    }
    disconnectedCallback() {
      this.ro?.disconnect();
      if (this.classList.contains('abar--sticky')) document.documentElement.style.removeProperty('--abar-sticky-h');
      this.stop();
      this.section?.removeEventListener('shopify:block:select', this.onBlock);
      this.section?.removeEventListener('shopify:block:deselect', this.onDeselect);
    }
    current() { return this.slides[this.index]; }
    visible(i) { return !this.slides[i].querySelector('.abar__item[hidden]'); }
    go(i) {
      const n = this.slides.length;
      let next = ((i % n) + n) % n;
      for (let k = 0; k < n && !this.visible(next); k++) next = (((i > this.index ? next + 1 : next - 1) % n) + n) % n;
      if (next === this.index) return;
      const prev = this.slides[this.index];
      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      if (!this.reduced) { prev.classList.add('is-leaving'); setTimeout(() => prev.classList.remove('is-leaving'), 650); }
      this.index = next;
      const cur = this.slides[next];
      cur.classList.add('is-active');
      cur.removeAttribute('aria-hidden');
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() {
      this.stop();
      if (this.hasAttribute('data-no-autoplay')) return;
      if (this.contains(document.activeElement) && document.activeElement !== document.body) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
})();
